import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FilterRule } from '@/lib/tableFilters/filterRuleTypes';
import { isRuleEmpty } from '@/lib/tableFilters/filterRuleEmpty';
import { normalizeMultiPersonRule } from '@/lib/tableFilters/primitives';

export interface PersistedColumnFiltersOptions {
  enabledKey: string;
  filtersKey: string;
  /** Постобработка правила при загрузке из JSON (миграции, нормализация). */
  normalizeLoadedRule?: (columnId: string, rule: FilterRule) => FilterRule;
}

function loadBoolean(key: string, defaultValue = false): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return raw === 'true';
  } catch {
    return defaultValue;
  }
}

function saveBoolean(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

function coerceRule(rule: unknown): FilterRule | null {
  if (!rule || typeof rule !== 'object' || !('type' in rule)) return null;
  return rule as FilterRule;
}

export function sanitizeReaderTagsFacetRule(
  rule: Extract<FilterRule, { type: 'readerTagsFacet' }>
): FilterRule {
  const facets: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(rule.facets ?? {})) {
    if (!Array.isArray(v)) continue;
    facets[k] = v.filter((x) => typeof x === 'string');
  }
  return { type: 'readerTagsFacet', facets };
}

function loadFiltersMap(
  filtersKey: string,
  normalizeLoadedRule?: (columnId: string, rule: FilterRule) => FilterRule
): Record<string, FilterRule> {
  try {
    const raw = localStorage.getItem(filtersKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, FilterRule> = {};
    for (const [col, rawRule] of Object.entries(parsed)) {
      const rule = coerceRule(rawRule);
      if (!rule) continue;
      const normalized = normalizeLoadedRule
        ? normalizeLoadedRule(col, rule)
        : normalizeMultiPersonRule(rule);
      out[col] = normalized;
    }
    return out;
  } catch {
    return {};
  }
}

function saveFiltersMap(filtersKey: string, filters: Record<string, FilterRule>): void {
  try {
    localStorage.setItem(filtersKey, JSON.stringify(filters));
  } catch {
    /* ignore */
  }
}

export interface UsePersistedColumnFiltersReturn {
  filtersEnabled: boolean;
  toggleFiltersEnabled: () => void;
  setFiltersEnabled: (value: boolean) => void;
  filters: Record<string, FilterRule>;
  setFilter: (columnId: string, rule: FilterRule | null) => void;
  replaceFilters: (next: Record<string, FilterRule>) => void;
  clearAllFilters: () => void;
  activeFilterCount: number;
}

export function usePersistedColumnFilters(
  options: PersistedColumnFiltersOptions
): UsePersistedColumnFiltersReturn {
  const { enabledKey, filtersKey, normalizeLoadedRule } = options;

  const [filtersEnabled, setFiltersEnabled] = useState<boolean>(() => loadBoolean(enabledKey, true));
  const [filters, setFilters] = useState<Record<string, FilterRule>>(() =>
    loadFiltersMap(filtersKey, normalizeLoadedRule)
  );

  useEffect(() => {
    saveBoolean(enabledKey, filtersEnabled);
  }, [enabledKey, filtersEnabled]);

  useEffect(() => {
    saveFiltersMap(filtersKey, filters);
  }, [filtersKey, filters]);

  const toggleFiltersEnabled = useCallback(() => {
    setFiltersEnabled((prev) => !prev);
  }, []);

  const setFiltersEnabledValue = useCallback((value: boolean) => {
    setFiltersEnabled(value);
  }, []);

  const setFilter = useCallback((columnId: string, rule: FilterRule | null) => {
    setFilters((prev) => {
      if (rule === null) {
        const next = { ...prev };
        delete next[columnId];
        return next;
      }
      const normalized =
        rule.type === 'readerTagsFacet'
          ? sanitizeReaderTagsFacetRule(rule)
          : normalizeMultiPersonRule(rule);
      if (isRuleEmpty(normalized)) {
        const next = { ...prev };
        delete next[columnId];
        return next;
      }
      return { ...prev, [columnId]: normalized };
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const replaceFilters = useCallback((next: Record<string, FilterRule>) => {
    setFilters(
      Object.fromEntries(
        Object.entries(next)
          .map(([col, r]) => {
            const norm =
              r.type === 'readerTagsFacet' ? sanitizeReaderTagsFacetRule(r) : normalizeMultiPersonRule(r);
            return [col, norm] as const;
          })
          .filter(([, r]) => !isRuleEmpty(r))
      )
    );
  }, []);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((r) => !isRuleEmpty(r)).length;
  }, [filters]);

  return {
    filtersEnabled,
    toggleFiltersEnabled,
    setFiltersEnabled: setFiltersEnabledValue,
    filters,
    setFilter,
    replaceFilters,
    clearAllFilters,
    activeFilterCount,
  };
}
